/**
 * KOVA · shared 404 / "workspace not ready" screen.
 *
 * Two flavours in one file:
 *
 *   <NotFound />             → default 404 landing, used as the catch-all
 *                              route in App.js.
 *
 *   <WorkspaceNotReady />    → for known routes whose backend / module
 *                              isn't wired up yet (locked module). Uses the
 *                              same visual language + a subtle "hydrating"
 *                              skeleton so it feels intentional, not broken.
 *
 * Both are built from the shared EmptyState + KovaSkeleton primitives so
 * they inherit the KOVA tokens automatically.
 */
import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/EmptyState";
import { SkeletonLine } from "@/components/KovaSkeleton";
import {
  Compass, ArrowLeft, LayoutDashboard, LifeBuoy, Wrench, HardHat,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  <NotFound />                                                      */
/* ------------------------------------------------------------------ */
export default function NotFound() {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="p-8 lg:p-10 max-w-[900px] mx-auto" data-testid="page-not-found">
      <div className="mb-6 pb-5 border-b border-[#3A4640]">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="tzat-pulse" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#39FF14] font-heading font-semibold">
            Not found · 404
          </div>
        </div>
        <h1 className="font-heading text-3xl lg:text-[32px] font-bold text-[#F8F9FA] tracking-tight">
          We couldn&rsquo;t find that page in KOVA.
        </h1>
        <p className="text-[#8A8F98] mt-1.5 text-sm max-w-2xl">
          The URL{" "}
          <span className="font-mono text-[#F8F9FA] bg-[#121815] border border-[#3A4640] px-1.5 py-0.5 rounded">
            {location.pathname}
          </span>{" "}
          didn&rsquo;t match any known module. Nothing to worry about — pick a destination below.
        </p>
      </div>

      <EmptyState
        icon={Compass}
        title="Let&rsquo;s get you back on track"
        description="Return to your dashboard, use Ctrl+K to jump to any customer / invoice, or head to a specific module."
        action={
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              className="bg-transparent border-[#3A4640] text-[#F8F9FA] hover:border-[#39FF14]/40 rounded-[10px]"
              data-testid="notfound-back-btn"
            >
              <ArrowLeft size={14} className="mr-2" /> Go back
            </Button>
            <Link to="/">
              <Button
                className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] font-semibold rounded-[10px]"
                data-testid="notfound-dashboard-btn"
              >
                <LayoutDashboard size={14} className="mr-2" /> Open Dashboard
              </Button>
            </Link>
          </div>
        }
        testid="notfound-hero"
      />

      {/* Quick jumps */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6" data-testid="notfound-quick-jumps">
        {[
          { to: "/customers",   label: "Customers",     hint: "Master customer file" },
          { to: "/sales",       label: "Sales",         hint: "Invoices & payments" },
          { to: "/engagement",  label: "Engagement",    hint: "Calling desk & campaigns" },
        ].map((q) => (
          <Link
            key={q.to}
            to={q.to}
            className="tzat-surface p-4 hover:border-[#39FF14]/40 transition-colors group"
            data-testid={`notfound-jump-${q.label.toLowerCase()}`}
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] font-heading font-semibold">
              Jump to
            </div>
            <div className="font-heading font-semibold text-[#F8F9FA] mt-1 group-hover:text-[#39FF14] transition-colors">
              {q.label}
            </div>
            <div className="text-xs text-[#8A8F98] mt-0.5">{q.hint}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  <WorkspaceNotReady />                                             */
/* ------------------------------------------------------------------ */
export function WorkspaceNotReady({
  title = "This workspace isn't ready yet",
  moduleName = "This module",
  description,
  eyebrow = "Coming soon",
  helpHref,
  helpLabel = "Talk to Admin",
  showBack = true,
}) {
  const navigate = useNavigate();
  const finalDescription =
    description ||
    `${moduleName} is being wired into KOVA. You'll see a skeleton preview here so you know it's on the way — no data has been created yet.`;

  return (
    <div className="p-8 lg:p-10 max-w-[1100px] mx-auto" data-testid="page-workspace-not-ready">
      <div className="mb-6 pb-5 border-b border-[#3A4640]">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="tzat-pulse" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#39FF14] font-heading font-semibold">
            {eyebrow}
          </div>
        </div>
        <h1 className="font-heading text-3xl lg:text-[32px] font-bold text-[#F8F9FA] tracking-tight">
          {title}
        </h1>
        <p className="text-[#8A8F98] mt-1.5 text-sm max-w-2xl">{finalDescription}</p>
      </div>

      <EmptyState
        icon={HardHat}
        title={`${moduleName} is coming to KOVA`}
        description="We're keeping the surface here so future links stay stable. When the module goes live your existing bookmarks will just start working."
        action={
          <div className="flex flex-wrap items-center justify-center gap-2">
            {showBack && (
              <Button
                onClick={() => navigate(-1)}
                variant="outline"
                className="bg-transparent border-[#3A4640] text-[#F8F9FA] hover:border-[#39FF14]/40 rounded-[10px]"
                data-testid="workspace-back-btn"
              >
                <ArrowLeft size={14} className="mr-2" /> Go back
              </Button>
            )}
            {helpHref ? (
              <a href={helpHref}>
                <Button
                  className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] font-semibold rounded-[10px]"
                  data-testid="workspace-help-btn"
                >
                  <LifeBuoy size={14} className="mr-2" /> {helpLabel}
                </Button>
              </a>
            ) : (
              <Link to="/">
                <Button
                  className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] font-semibold rounded-[10px]"
                  data-testid="workspace-dashboard-btn"
                >
                  <LayoutDashboard size={14} className="mr-2" /> Open Dashboard
                </Button>
              </Link>
            )}
          </div>
        }
        testid="workspace-hero"
      />

      {/* Skeleton preview — signals "this surface will look like this once wired up". */}
      <div className="mt-6" data-testid="workspace-skeleton-preview">
        <div className="flex items-center gap-2 mb-3">
          <Wrench size={12} className="text-[#39FF14]" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] font-heading font-semibold">
            Layout preview
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="tzat-kpi opacity-60">
              <SkeletonLine w={90} h={9} className="mb-3" />
              <SkeletonLine w={120} h={22} />
            </div>
          ))}
        </div>
        <div className="tzat-card p-6 opacity-60">
          <SkeletonLine w={160} h={14} className="mb-4" />
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex gap-3 py-2 border-b border-[#3A4640] last:border-0">
              <SkeletonLine w={70} h={10} />
              <SkeletonLine w="70%" h={10} className="flex-1" />
              <SkeletonLine w={90} h={10} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
